import { Button, Color, Flex, Icon, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { INTENT_CHOICES } from "../store/intent";
import { AgentIntent } from "../store/types";
import { ReviewButton } from "../../../pages/SpacePlanning/review/reviewDesign";
import { ReviewColor } from "../../../pages/SpacePlanning/review/reviewDesignTokens";

interface Props {
  embedded?: boolean;
  prompt: string;
  /** Only the newest turn can still be answered; older pickers are shown settled. */
  isDisabled: boolean;
  onPick: (intent: AgentIntent) => void;
}

/**
 * Shown in place of an agent reply when the agent could not tell what the
 * prompt asks for. Picking re-sends the same prompt with the intent forced.
 */
export const AgentIntentPicker = ({ embedded = false, prompt, isDisabled, onPick }: Props) => {
  const { t } = useTranslation("pogAgent");
  const ChoiceButton = embedded ? ReviewButton : Button;

  return (
    <Flex
      column
      gap="8px"
      padding="10px 12px"
      background={Color.white}
      borderColor={embedded ? ReviewColor.line : Color.spaceGray}
      borderRadius={embedded ? "12px" : "4px"}
      maxWidth="100%"
      data-testid="pog-agent-intent-picker"
    >
      <Flex gap="6px" align="center">
        <Icon name="info" color={Color.primary} size={14} />

        <Text variant="body3" wordBreak="break-word">
          {t("picker.question")}
        </Text>
      </Flex>

      <Text variant="small2" color={Color.textSecondary} wordBreak="break-word">
        “{prompt}”
      </Text>

      <Flex gap="6px" flexWrap="wrap">
        {INTENT_CHOICES.map((choice) => (
          <ChoiceButton
            key={choice.intent}
            size="small"
            color={choice.isAvailable ? "primary" : "greenSmoke"}
            variant={choice.isAvailable ? "default" : "inverted"}
            isDisabled={isDisabled || !choice.isAvailable}
            onClick={() => onPick(choice.intent)}
          >
            {t(`picker.${choice.intent}`)}
          </ChoiceButton>
        ))}
      </Flex>
    </Flex>
  );
};
